import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Box,
  Typography,
  ButtonGroup,
  Button,
  CircularProgress,
} from "@material-ui/core";
import "./chartConfig";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { TooltipItem } from "chart.js";
import { getCreatorUploadVolumeAPI } from "../../../../api/dashbordApi";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chartContainer: {
      height: "300px",
      width: "100%",
      position: "relative",
      backgroundColor: "#1a1a1a",
      borderRadius: "8px",
      padding: "16px",
      [theme.breakpoints.down("md")]: {
        height: "250px",
      },
      [theme.breakpoints.down("sm")]: {
        height: "200px",
      },
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    title: {
      color: "#FFFFFF",
      fontSize: "18px",
      fontWeight: 600,
    },
    buttonGroup: {
      boxShadow: "none",
      "& .MuiButtonGroup-grouped:not(:last-of-type)": {
        borderRightColor: "#FE5A97",
      },
      "& .MuiButton-root": {
        textTransform: "none",
        fontWeight: 600,
        fontSize: "14px",
        boxShadow: "none",
        padding: "6px 12px",
        minWidth: "64px",
      },
      "& .MuiButton-contained": {
        backgroundColor: "#FE5A97",
        borderColor: "#FE5A97",
        color: "#FFFFFF",
        "&:hover": {
          backgroundColor: "#e54d88",
          borderColor: "#e54d88",
          boxShadow: "none",
        },
      },
      "& .MuiButton-outlined": {
        color: "#FFFFFF",
        borderColor: "#FE5A97",
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: "rgba(254, 90, 151, 0.08)",
          borderColor: "#FE5A97",
        },
      },
    },
    loadingContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      color: "#FFFFFF",
    },
    errorContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      color: "#FF6B6B",
    },
  })
);

export interface IUploadVolumeData {
  date: string;
  photos: number;
  videos: number;
  total: number;
  galleryPosts: number;
  videoRequests: number;
  totalUploads: number;
}

type ContentUploadVolumeChartProps = {
  className?: string;
};

const ContentUploadVolumeChart: React.FC<ContentUploadVolumeChartProps> = ({
  className,
}) => {
  const classes = useStyles();
  const [viewType, setViewType] = useState<"daily" | "weekly" | "monthly">(
    "daily"
  );
  const [data, setData] = useState<IUploadVolumeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (granularity: "daily" | "weekly" | "monthly") => {
    setLoading(true);
    setError(null);

    try {
      const response = await (getCreatorUploadVolumeAPI as any)({
        granularity,
        preset:
          granularity === "daily"
            ? "day"
            : granularity === "weekly"
            ? "week"
            : "month",
        fillGaps: true,
      });

      if (response && response.data) {
        setData(response.data);
      } else {
        setError("No upload data available");
      }
    } catch (err) {
      console.error("Error fetching upload volume data:", err);
      setError("Failed to load upload data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(viewType);
  }, [viewType]);

  const handleViewChange = (newView: "daily" | "weekly" | "monthly") => {
    setViewType(newView);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#FFFFFF",
          boxWidth: 14,
          padding: 16,
          usePointStyle: true,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        borderColor: "#FE5A97",
        borderWidth: 1,
        callbacks: {
          label: (context: TooltipItem<"bar">) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value} uploads`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: "#FFFFFF",
          maxTicksLimit: 8,
        },
        grid: {
          color: "#2F2F2F",
          drawBorder: false,
        },
      },
      y: {
        stacked: true,
        ticks: {
          color: "#FFFFFF",
          stepSize: 1,
          callback: (value: string | number) => {
            const val = Number(value);
            return val === Math.floor(val) ? val.toString() : "";
          },
        },
        grid: {
          color: "#2F2F2F",
          drawBorder: false,
        },
        beginAtZero: true,
      },
    },
    elements: {
      bar: {
        borderRadius: 4,
        borderSkipped: false,
      },
    },
  };

  const chartData = {
    labels: data.map(item => {
      const date = new Date(item.date);
      if (viewType === "daily") {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      } else if (viewType === "weekly") {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "2-digit",
        });
      }
    }),
    datasets: [
      {
        label: "Photos",
        data: data.map(
          item => item.photos || item.galleryPosts || item?.totalUploads || 0
        ),
        backgroundColor: "#FE5A97",
        borderColor: "#FE5A97",
        borderWidth: 1,
        stack: "uploads",
      },
      {
        label: "Videos",
        data: data.map(item => item.videos || item.videoRequests || 0),
        backgroundColor: "#FFD700",
        borderColor: "#FFD700",
        borderWidth: 1,
        stack: "uploads",
      },
    ],
  };

  if (loading) {
    return (
      <Box className={`${classes.chartContainer} ${className || ""}`}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} style={{ color: "#FE5A97" }} />
          <Typography style={{ marginLeft: "12px" }}>
            Loading upload data...
          </Typography>
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={`${classes.chartContainer} ${className || ""}`}>
        <div className={classes.errorContainer}>
          <Typography>{error}</Typography>
        </div>
      </Box>
    );
  }

  return (
    <Box className={`${classes.chartContainer} ${className || ""}`}>
      <div className={classes.header}>
        <Typography className={classes.title}>Content Upload Volume</Typography>
        <ButtonGroup
          className={classes.buttonGroup}
          size="small"
          variant="outlined"
        >
          <Button
            onClick={() => handleViewChange("daily")}
            variant={viewType === "daily" ? "contained" : "outlined"}
          >
            Daily
          </Button>
          <Button
            onClick={() => handleViewChange("weekly")}
            variant={viewType === "weekly" ? "contained" : "outlined"}
          >
            Weekly
          </Button>
          <Button
            onClick={() => handleViewChange("monthly")}
            variant={viewType === "monthly" ? "contained" : "outlined"}
          >
            Monthly
          </Button>
        </ButtonGroup>
      </div>

      {data.length > 0 ? (
        <Bar data={chartData} options={chartOptions} />
      ) : (
        <div className={classes.loadingContainer}>
          <Typography>No upload data available</Typography>
        </div>
      )}
    </Box>
  );
};

export default ContentUploadVolumeChart;
